import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, ArrowRight, RefreshCw, Trash2, Pencil, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, formatApiError } from "../lib/api";

/** Owner-only panel. 3 fixed device slots. Each slot has a 6-digit
 *  activation code the admin shares with the device owner. The user
 *  types the code once on their phone (MobileGate) to bind the slot. */
export default function AdminDevices() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [edit, setEdit] = useState(null);  // {id, label}
    const [copied, setCopied] = useState(null);
    const [empOptions, setEmpOptions] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/devices");
            setItems(r.data.items || []);
            const o = await api.get("/admin/devices/employee-options");
            setEmpOptions(o.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const regenerate = async (id) => {
        if (!window.confirm("توليد كود جديد سيلغي ربط الجهاز الحالي بهذه الخانة. المتابعة؟")) return;
        setBusy(id);
        try { await api.post(`/admin/devices/${id}/regenerate-code`); toast.success("تم توليد كود جديد"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const revoke = async (id) => {
        if (!window.confirm("إلغاء ربط الجهاز الحالي بهذه الخانة؟")) return;
        setBusy(id);
        try { await api.post(`/admin/devices/${id}/revoke`); toast.success("تم إلغاء ربط الجهاز"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const saveLabel = async () => {
        if (!edit?.label?.trim()) return;
        setBusy(edit.id);
        try {
            await api.patch(`/admin/devices/${edit.id}`, { label: edit.label.trim() });
            setEdit(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const assignEmployee = async (id, employee_id) => {
        setBusy(id);
        try {
            await api.patch(`/admin/devices/${id}`, { employee_id });
            toast.success(employee_id ? "تم ربط الجهاز بالموظف" : "تم إلغاء تخصيص الموظف");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    const copy = async (code, id) => {
        try { await navigator.clipboard.writeText(code); setCopied(id); setTimeout(() => setCopied(null), 1500); }
        catch { /* ignore */ }
    };

    return (
        <div className="min-h-screen bg-neutral-50 p-4" dir="rtl">
            <div className="mx-auto max-w-3xl space-y-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/accounts")} className="gap-1" data-testid="admin-devices-back">
                        <ArrowRight className="h-4 w-4" />
                        الرجوع
                    </Button>
                    <h1 className="font-heading text-2xl font-bold text-neutral-900">أجهزة الجوال المعتمدة</h1>
                </div>
                <p className="text-sm text-neutral-500">
                    6 خانات ثابتة. شارك كود الاعتماد المكوَّن من 6 أرقام مع صاحب الهاتف. يفتح معيار من الجوال ثم يُدخل الكود مرة واحدة فقط. بعد الاستخدام يتجدد الكود تلقائياً.
                </p>

                {loading ? (
                    <div className="py-10 text-center text-sm text-neutral-500">...</div>
                ) : (
                    <div className="space-y-3">
                        {items.map((s) => (
                            <div key={s.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-neutral-200" data-testid={`device-slot-${s.slot_number}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${s.is_bound ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                                            <Smartphone className="h-5 w-5" />
                                        </div>
                                        <div>
                                            {edit?.id === s.id ? (
                                                <div className="flex items-center gap-1">
                                                    <Input
                                                        autoFocus
                                                        value={edit.label}
                                                        onChange={(e) => setEdit({ ...edit, label: e.target.value })}
                                                        className="h-8 w-40 text-sm"
                                                        data-testid={`device-label-input-${s.slot_number}`}
                                                    />
                                                    <Button size="sm" onClick={saveLabel} disabled={busy === s.id} data-testid={`device-label-save-${s.slot_number}`}>حفظ</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>إلغاء</Button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-1 font-heading text-base font-bold">
                                                        {s.label}
                                                        <button onClick={() => setEdit({ id: s.id, label: s.label })} className="rounded p-1 hover:bg-neutral-100" title="تعديل الاسم" data-testid={`device-edit-${s.slot_number}`}>
                                                            <Pencil className="h-3.5 w-3.5 text-neutral-400" />
                                                        </button>
                                                    </div>
                                                    <div className="text-[11px] text-neutral-500">
                                                        خانة {s.slot_number} —{" "}
                                                        {s.is_bound ? (
                                                            <span className="font-bold text-emerald-700">
                                                                مربوطة بجهاز {s.last_seen_at ? `• آخر دخول ${new Date(s.last_seen_at).toLocaleString("ar-SA")}` : ""}
                                                            </span>
                                                        ) : (
                                                            <span className="text-neutral-500">غير مربوطة بأي جهاز</span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                                    <span className="text-neutral-600">الموظف المخصص:</span>
                                    <select
                                        value={s.employee_id || ""}
                                        onChange={(e) => assignEmployee(s.id, e.target.value)}
                                        disabled={busy === s.id}
                                        className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm"
                                        data-testid={`device-employee-select-${s.slot_number}`}
                                    >
                                        <option value="">— بدون تخصيص —</option>
                                        {s.employee_id && !empOptions.some((o) => o.id === s.employee_id) ? (
                                            <option value={s.employee_id}>{s.employee_name || "موظف"}</option>
                                        ) : null}
                                        {empOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                                    </select>
                                    {s.employee_name ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-900" data-testid={`device-employee-name-${s.slot_number}`}>{s.employee_name}</span> : null}
                                </div>
                                {!s.is_bound ? (
                                    <div className="mt-4 flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200">
                                        <div className="flex-1">
                                            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-900">كود الاعتماد</div>
                                            <div className="mt-0.5 font-mono text-3xl font-bold tracking-[0.3em] text-amber-900" dir="ltr" data-testid={`device-code-${s.slot_number}`}>
                                                {s.activation_code}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => copy(s.activation_code, s.id)}
                                            className="rounded-lg bg-white p-2 text-amber-700 ring-1 ring-amber-300 hover:bg-amber-100"
                                            title="نسخ"
                                            data-testid={`device-copy-${s.slot_number}`}
                                        >
                                            {copied === s.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                        </button>
                                    </div>
                                ) : null}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => regenerate(s.id)}
                                        disabled={busy === s.id}
                                        className="gap-1 text-[12px]"
                                        data-testid={`device-regen-${s.slot_number}`}
                                    >
                                        {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                        توليد كود جديد
                                    </Button>
                                    {s.is_bound ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => revoke(s.id)}
                                            disabled={busy === s.id}
                                            className="gap-1 text-[12px] text-red-700 hover:bg-red-50"
                                            data-testid={`device-revoke-${s.slot_number}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            إلغاء ربط الجهاز
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
